import React, { useState, useEffect, useRef } from 'react'
import { Map } from 'immutable'
import _get from 'lodash/get'
import { connect as connectRedux } from 'react-redux'
import { getBoundActions } from '../../actions/index'
import { isIOS } from '../../environment/index'
import { getCookie, setCookie } from '../../services/cookie/index'
import { getAuthIsLoggedIn } from '../../services/auth/index'
import Button, { TYPES } from '../Button/index'
import CloseIcon from '../Icon/icons/Close/Close'
import { get as getConfig } from '../../config/index'
import {
  APP_OPEN_BANNER_CLICKED_GA4_EVENT,
  APP_OPEN_BANNER_DISMISSED_GA4_EVENT,
} from '../../services/event-tracking/index'
import { selectMemberHomeFirstTouch } from '../../services/app/selectors'
import { getDeepLink, checkShouldShowBanner } from './utils'

const config = getConfig()
const branchKey = _get(config, ['features', 'appDownloadBanner', 'branchKey'])
const STORAGE_KEY_DISMISSED = 'app_download_banner_dismissed'
/** Align with event-tracking middleware PUSH_DELAY_MS (750) so GA hits before navigation. */
const BANNER_ANALYTICS_NAV_DELAY_MS = 800

/**
 * App download banner on mobile.
 * @param {Object} props - The props
 * @param {Map} props.auth - The auth data
 * @param {Map} props.resolver - The resolver data
 * @param {Map} props.staticText - The static text data
 * @param {Function} props.setEventDefault - The function to set the event default
 * @param {boolean} props.suppressForFirstTouchNavOverlap - Hide while Member Home First Touch
 * @returns {JSX.Element|null}
 */
function AppDownloadBanner (props) {
  const {
    auth,
    resolver,
    staticText,
    setEventDefault,
    suppressForFirstTouchNavOverlap,
  } = props

  const ios = isIOS()
  const isLoggedIn = getAuthIsLoggedIn(auth)

  const [branchSdkLoaded, setBranchSdkLoaded] = useState(window && window.branch)
  const [mounted, setMounted] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [initialized, setInitialized] = useState(false)
  const [linkUrl, setLinkUrl] = useState(null)
  const [hasApp, setHasApp] = useState(false)
  const openNavTimeoutRef = useRef(null)

  const deepLink = getDeepLink(resolver)

  const showBanner = isLoggedIn && !dismissed && checkShouldShowBanner(resolver)
    && !suppressForFirstTouchNavOverlap

  const bannerClassName = [
    'app-download-banner',
    ios ? 'app-download-banner--ios' : 'app-download-banner--android',
  ].join(' ')

  const handleDismiss = () => {
    setEventDefault(APP_OPEN_BANNER_DISMISSED_GA4_EVENT)
    setDismissed(true)
    setCookie(STORAGE_KEY_DISMISSED, '1')
  }

  const handleOpenInApp = () => {
    setEventDefault(APP_OPEN_BANNER_CLICKED_GA4_EVENT)
    if (openNavTimeoutRef.current) {
      global.clearTimeout(openNavTimeoutRef.current)
    }
    openNavTimeoutRef.current = global.setTimeout(() => {
      openNavTimeoutRef.current = null
      global.location.href = linkUrl
    }, BANNER_ANALYTICS_NAV_DELAY_MS)
  }

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => () => {
    if (openNavTimeoutRef.current) {
      global.clearTimeout(openNavTimeoutRef.current)
    }
  }, [])

  useEffect(() => {
    if (!mounted) {
      return
    }
    const storedDismissed = getCookie(STORAGE_KEY_DISMISSED)
    if (storedDismissed === '1') {
      setDismissed(true)
    }
  }, [mounted])

  // Branch SDK is just a self-executing script, which creates a window.branch object
  // Load Branch SDK only on client to avoid "window is not defined" during SSR
  useEffect(() => {
    if (typeof window === 'undefined' || window.branch) {
      return
    }
    if (!mounted || !showBanner || branchSdkLoaded) {
      return
    }
    // eslint-disable-next-line import/no-unresolved
    import('branch-sdk').then(() => {
      setBranchSdkLoaded(true)
    }).catch(() => {
      setDismissed(true)
    })
  }, [mounted, showBanner, branchSdkLoaded])

  useEffect(() => {
    if (typeof window === 'undefined' || !window.branch) {
      return
    }
    if (branchSdkLoaded && !initialized && showBanner) {
      window.branch.init(branchKey, {
        no_journeys: true,
      }, (err, data) => {
        if (err) {
          setDismissed(true)
        } else {
          setInitialized(true)
          setHasApp(data.has_app || false)
        }
      })
    }
  }, [branchSdkLoaded, initialized, showBanner])

  useEffect(() => {
    if (typeof window === 'undefined' || !window.branch) {
      return
    }
    if (branchSdkLoaded && initialized && showBanner) {
      window.branch.link({
        data: {
          $deeplink_path: deepLink,
        },
      }, (err, link) => {
        if (err) {
          setLinkUrl(null)
        } else {
          setLinkUrl(link)
        }
      })
    }
  }, [branchSdkLoaded, initialized, deepLink, showBanner])

  if (!showBanner || !mounted || !initialized || !linkUrl) {
    return null
  }

  return (
    <div
      className={bannerClassName}
      role="banner"
      aria-label={staticText.get('appName')}
    >
      <div className="app-download-banner__inner">
        <Button
          type={TYPES.ICON}
          iconSvg={<CloseIcon size={20} />}
          onClick={handleDismiss}
          className="app-download-banner__dismiss"
          aria-label={staticText.get('dismissAriaLabel')}
        />
        <div className="app-download-banner__app-logo" />
        <div className="app-download-banner__meta">
          <span className="app-download-banner__name">{staticText.get('appName')}</span>
          <span className="app-download-banner__cta">
            {hasApp ? staticText.get('openInApp') : staticText.get('getTheApp')}
          </span>
        </div>
      </div>
      <Button type={TYPES.PILL} className="app-download-banner__button" onClick={handleOpenInApp}>
        {hasApp ? staticText.get('openButton') : staticText.get('getButton')}
      </Button>
    </div>
  )
}

export default connectRedux((state) => {
  const header = Map.isMap(state.header) ? state.header : Map()
  const isMemberHomeFirstTouchVisible = !!selectMemberHomeFirstTouch(state).get('visible')
  const anyPrimaryNavMenuOpen = !!(
    header.get('navigationOverlayVisible')
    || header.get('userMenuVisible')
    || header.get('notificationsMenuVisible')
  )
  return {
    auth: state.auth,
    resolver: state.resolver,
    staticText: state.staticText.getIn(['data', 'appDownloadBanner', 'data'], Map()),
    suppressForFirstTouchNavOverlap: isMemberHomeFirstTouchVisible && anyPrimaryNavMenuOpen,
  }
}, (dispatch) => {
  const actions = getBoundActions(dispatch)
  return {
    setEventDefault: actions.eventTracking.setEventDefault,
  }
})(AppDownloadBanner)
